import type { Company } from '../@types/Company';
import { getCompanyDisplayName } from './company';
import { JWTAuthorization } from './JWTHeader';

interface ShortUrlResult {
  shortUrl: string;
  shortId: string;
  id: string;
}

/**
 * Convert a string into a Redirector-compatible slug prefix.
 * Rules: lowercase alphanumeric + hyphens, 1-100 chars.
 */
function toSlugPrefix(value: string): string {
  return value
    .normalize('NFD')
    .replaceAll(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .replaceAll(/[^a-z0-9]+/g, '-')
    .replaceAll(/^-+|-+$/g, '')
    .slice(0, 100);
}

/**
 * Derive the slug prefix from a Company's branding commercial name,
 * falling back to the legal company name.
 */
export function getCompanySlugPrefix(company: Company | null | undefined): string {
  const displayName = getCompanyDisplayName(company);
  if (!displayName) {
    return 'consentement';
  }
  const slug = toSlugPrefix(displayName);
  return slug || 'consentement';
}

/**
 * Shorten a single URL via the backend Redirector service.
 * Falls back to the original URL on failure so the app remains functional.
 */
export async function shortenUrl(
  originalUrl: string,
  jwt: string | null,
  slugPrefix?: string,
): Promise<string> {
  try {
    const body: Record<string, string> = { url: originalUrl };
    if (slugPrefix) {
      body.slugPrefix = slugPrefix;
    }

    const response = await fetch('/api/short-urls', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: JWTAuthorization(jwt),
      },
      body: JSON.stringify(body),
    });

    if (!response.ok) {
      console.warn('[shortUrl] Failed to shorten URL, using original:', originalUrl);
      return originalUrl;
    }

    const data: ShortUrlResult = await response.json();
    return data.shortUrl;
  } catch (error) {
    console.warn('[shortUrl] Error shortening URL, using original:', error);
    return originalUrl;
  }
}

/**
 * Build a consent URL for an owner and shorten it via the Redirector service.
 * The slug prefix is derived from the company's commercial name (branding),
 * falling back to the company's legal name.
 */
export async function buildShortConsentUrl(
  company: Company | null | undefined,
  ownerUuid: string,
  jwt: string | null,
): Promise<string> {
  const siren = company?.siren;
  const originalUrl = `${window.location.origin}/consent/${siren}/owner/${ownerUuid}`;
  const slugPrefix = getCompanySlugPrefix(company);
  return shortenUrl(originalUrl, jwt, slugPrefix);
}

/**
 * Build a raw (non-shortened) consent URL.
 * Use this when you don't have a JWT or can't await.
 */
export function buildConsentUrl(
  siren: string | undefined,
  ownerUuid: string,
): string {
  return `${window.location.origin}/consent/${siren}/owner/${ownerUuid}`;
}
